import { ChevronDown, Circle, Lock } from "lucide-react";

import { speciesName } from "@/data/speciesNames";
import {
  VariantBadge,
  variantColor as vColor,
  variantAlpha as vAlpha,
} from "@/components/dexVariantVisuals";
import { CompareSprite } from "@/components/trade/CompareSprite";
import { formDisplayName, type PanelRow } from "@/components/trade/compareTypes";

/**
 * One compact lead plate in the "you have / they have" lists. Tapping it adds
 * the lead to the trade list. Kept deliberately spare — a single circle at the
 * LEFT (the add affordance), the variant art, the name, and the dex-variant
 * badge — so two fit per row. BOTH lists use it, so both read in the same
 * per-VARIANT colour language (the friend-hue tint the "they have" list used is
 * gone). `row.variant` is a CompareVariant, a subset of DexVariantKey.
 */
export function LeadTile({ row, onAdd, testId }: { row: PanelRow; onAdd: () => void; testId: string }) {
  const id = row.id;
  const formLabel = formDisplayName(id, row.formId);
  return (
    <button
      data-testid={testId}
      onClick={onAdd}
      className="text-left rounded-xl min-w-0"
      style={{
        background: "var(--surface-dark-7)",
        border: `1px solid ${vAlpha(row.variant, 0.4)}`,
      }}
    >
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        {/* Single circle at the left — the add affordance. */}
        <Circle className="w-4 h-4 shrink-0" style={{ color: vColor(row.variant) }} />
        <div
          className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center overflow-clip"
          style={{ background: "var(--surface-dark-5)", border: `1px solid ${vAlpha(row.variant, 0.4)}` }}
        >
          <CompareSprite id={id} shiny={row.isShiny} size={26} formId={row.formId} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-[12px] leading-tight truncate" style={{ color: "var(--text-ink-bright)" }}>
            {speciesName(id)}
          </p>
          <div className="mt-0.5 flex items-center gap-1 flex-wrap">
            <VariantBadge variant={row.variant} />
            {/* Without this a form lead was indistinguishable from the plain
                species — two tiles reading "Vulpix", one of them Alolan. */}
            {formLabel && (
              <span
                className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full leading-none"
                style={{
                  color: vColor(row.variant),
                  background: vAlpha(row.variant, 0.14),
                  border: `1px solid ${vAlpha(row.variant, 0.4)}`,
                }}
                data-testid={`${testId}-form`}
              >
                {formLabel}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

/**
 * "View all / Show fewer" toggle shown under a surfaced list once it exceeds the
 * top-8 preview. Renders nothing when there is nothing hidden.
 */
export function ViewAllButton({
  total,
  showingAll,
  onToggle,
  testId = "compare-view-all",
}: {
  total: number;
  showingAll: boolean;
  onToggle: () => void;
  testId?: string;
}) {
  if (total <= 8) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testId}
      className="mt-1 w-full rounded-full text-[11px] font-semibold py-2 inline-flex items-center justify-center gap-1.5"
      style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow)", color: "var(--text-primary)", minHeight: 34 }}
    >
      {showingAll ? "Show fewer" : `View all ${total}`}
      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showingAll ? "rotate-180" : ""}`} />
    </button>
  );
}

/**
 * Basic-tier tease shown in place of ViewAllButton: the top FREE_COMPARE_LEADS
 * leads render normally and the rest are locked behind Pro. Tapping opens the
 * paywall (the same `trade-matching` reason the mutual-trades feed uses).
 */
export function ProTradeTease({ hidden, onUnlock }: { hidden: number; onUnlock: () => void }) {
  return (
    <button
      type="button"
      onClick={onUnlock}
      data-testid="compare-pro-tease"
      className="mt-1 w-full rounded-full text-[11px] font-semibold py-2 inline-flex items-center justify-center gap-1.5"
      style={{
        background: "var(--surface-dark-9)",
        border: "1px solid var(--accent-fill-glow)",
        color: "var(--accent-fill)",
        minHeight: 34,
      }}
    >
      <Lock className="w-3.5 h-3.5" />
      Unlock {hidden} more with Pro
    </button>
  );
}
